import {
  AutoAwesome,
  Close,
  Info,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  OpenInNew,
  ZoomIn,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import React, { useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const ProjectModal = ({ open, project, onClose }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const intl = useIntl();

  // Generate image paths with dynamic labels from description and capabilities
  // Must be called unconditionally (before early return) to maintain hook order
  const images = useMemo(() => {
    if (!project || !project.imageCount) {
      return [];
    }
    const imageList = [];
    // Get project description and capabilities for labels
    const introKey = `projects.${project.id}.introduction`;
    const introText = intl.formatMessage(
      {
        id: introKey,
        defaultMessage: `Introduction for ${project.id} project.`,
      },
      {}
    );

    // Extract key phrases from description (split by sentences, take meaningful parts)
    const introPhrases = introText
      .split(/[.!?]/)
      .filter((s) => s.trim().length > 20)
      .map((s) => s.trim().substring(0, 60))
      .slice(0, project.imageCount);

    // Get capability labels
    const capabilityLabels = [];
    if (project.workflows && project.workflows.length > 0) {
      for (const workflow of project.workflows) {
        const workflowKey = workflow.key;
        for (const stepKey of workflow.stepKeys) {
          const stepId = `projects.${project.id}.workflows.${workflowKey}.steps.${stepKey}`;
          const stepLabel = intl.formatMessage(
            {
              id: stepId,
              defaultMessage: stepKey,
            },
            {}
          );
          capabilityLabels.push(stepLabel);
        }
      }
    }

    // Combine and shuffle labels
    const allLabels = [...introPhrases, ...capabilityLabels].filter(Boolean);

    for (let i = 1; i <= project.imageCount; i++) {
      // Cycle through labels or use default
      const labelIndex = (i - 1) % allLabels.length;
      const label =
        allLabels[labelIndex] ||
        intl.formatMessage(
          {
            id: 'projectModal.feature',
            defaultMessage: 'Feature {number}',
          },
          { number: i }
        );

      imageList.push({
        path: `/${project.folder}/${i}.png`,
        label: label.length > 50 ? `${label.substring(0, 50)}...` : label,
      });
    }
    return imageList;
  }, [project, intl]);

  // Keyboard navigation for images - MUST be called before early return
  React.useEffect(() => {
    if (!lightboxOpen || images.length === 0) return;

    const handleKeyDown = (e) => {
      const imageCount = images.length;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setSelectedImageIndex((prev) => (prev === null ? 0 : (prev - 1 + imageCount) % imageCount));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setSelectedImageIndex((prev) => (prev === null ? 0 : (prev + 1) % imageCount));
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setLightboxOpen(false);
        setSelectedImageIndex(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, images]);

  // Early return if no project (after ALL hooks)
  if (!project) {
    return null;
  }

  const handleOpenWebsite = () => {
    window.open(project.url, '_blank');
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setSelectedImageIndex(null);
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Get project gradient colors
  const gradientColors = project?.gradient || ['#7c4dff', '#5e35b1'];
  const gradientBackground = `linear-gradient(135deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="project-modal-title"
      aria-describedby="project-modal-description"
      PaperProps={{
        sx: {
          background: gradientBackground,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.4)',
            zIndex: 0,
          },
        },
      }}
    >
      <DialogTitle id="project-modal-title" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 600,
              color: 'white',
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            }}
          >
            {intl.formatMessage({
              id: `projects.${project.id}.name`,
              defaultMessage: project.id,
            })}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label={intl.formatMessage({
              id: 'projectModal.close',
              defaultMessage: 'Close',
            })}
            sx={{
              color: '#ffffff',
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.6)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.8)',
                borderColor: 'rgba(255, 255, 255, 0.9)',
                color: '#ffffff',
                transform: 'scale(1.1)',
              },
              '&:focus-visible': {
                outline: '3px solid rgba(255, 255, 255, 0.9)',
                outlineOffset: '2px',
              },
            }}
          >
            <Close sx={{ fontSize: { xs: 24, sm: 28 } }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        id="project-modal-description"
        dividers
        sx={{
          position: 'relative',
          zIndex: 1,
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          '& .MuiDivider-root': {
            borderColor: 'rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {/* Responsive Screenshot Gallery Grid */}
        {images.length > 0 ? (
          <Box>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: 600,
                color: 'rgba(0, 0, 0, 0.87)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 3,
                fontSize: { xs: '1rem', sm: '1.125rem' },
              }}
            >
              <ZoomIn sx={{ fontSize: { xs: 20, sm: 24 }, color: gradientColors[0] }} />
              <FormattedMessage id="projectModal.gallery" defaultMessage="Gallery" />
            </Typography>

            {/* Responsive Grid Container */}
            <Grid
              container
              component="section"
              aria-label={intl.formatMessage({
                id: 'projectModal.gallery',
                defaultMessage: 'Gallery',
              })}
              spacing={{ xs: 2, sm: 2.5, md: 3 }}
            >
              {images.map((image, index) => (
                <Grid item xs={12} sm={6} md={4} key={image.path}>
                  <Box
                    component="button"
                    aria-label={intl.formatMessage(
                      {
                        id: 'projectModal.openImage',
                        defaultMessage: 'Open image {number} in full screen',
                      },
                      { number: index + 1 }
                    )}
                    onClick={() => handleImageClick(index)}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      aspectRatio: '16/9',
                      borderRadius: 2,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      p: 0,
                      '&:hover': {
                        transform: 'translateY(-4px) scale(1.02)',
                        boxShadow: `0 8px 24px ${gradientColors[0]}30`,
                        borderColor: gradientColors[0],
                        '& .zoom-overlay': {
                          opacity: 1,
                        },
                        '& .image-label': {
                          transform: 'translateY(0)',
                        },
                      },
                      '&:focus-visible': {
                        outline: '3px solid',
                        outlineColor: gradientColors[0],
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={image.path}
                      alt={image.label || ''}
                      loading="lazy"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Hover Overlay */}
                    <Box
                      className="zoom-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      }}
                    >
                      <ZoomIn sx={{ color: 'white', fontSize: { xs: 28, sm: 32 } }} />
                    </Box>
                    {/* Image Label Overlay */}
                    <Box
                      className="image-label"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.75)',
                        color: 'white',
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.75, sm: 1 },
                        backdropFilter: 'blur(10px)',
                        transform: 'translateY(100%)',
                        transition: 'transform 0.3s ease',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          fontWeight: 500,
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {image.label}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              px: 3,
              borderRadius: 2,
              bgcolor: 'rgba(0, 0, 0, 0.05)',
              border: '2px dashed',
              borderColor: 'rgba(0, 0, 0, 0.1)',
            }}
          >
            <ZoomIn sx={{ fontSize: 48, color: 'rgba(0, 0, 0, 0.3)', mb: 2 }} />
            <Typography variant="body1" sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>
              <FormattedMessage
                id="projectModal.noImages"
                defaultMessage="No images available for this project"
              />
            </Typography>
          </Box>
        )}

        {/* Lightbox Modal */}
        <Modal
          open={lightboxOpen}
          onClose={handleCloseLightbox}
          aria-labelledby="lightbox-title"
          aria-describedby="lightbox-description"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1400,
          }}
        >
          <Box
            component="dialog"
            aria-labelledby="lightbox-title"
            aria-describedby="lightbox-description"
            sx={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              bgcolor: 'rgba(0, 0, 0, 0.95)',
              borderRadius: 2,
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              id="lightbox-title"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                borderWidth: 0,
              }}
            >
              {intl.formatMessage({
                id: 'projectModal.gallery',
                defaultMessage: 'Gallery',
              })}
            </Typography>
            <Typography
              id="lightbox-description"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                borderWidth: 0,
              }}
            >
              {selectedImageIndex !== null &&
                intl.formatMessage(
                  {
                    id: 'projectModal.imageCounter',
                    defaultMessage: 'Image {current} of {total}',
                  },
                  {
                    current: selectedImageIndex + 1,
                    total: images.length,
                  }
                )}
            </Typography>
            {/* Close Button */}
            <IconButton
              onClick={handleCloseLightbox}
              aria-label={intl.formatMessage({
                id: 'projectModal.closeLightbox',
                defaultMessage: 'Close image viewer',
              })}
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 16 },
                right: { xs: 8, sm: 16 },
                color: '#ffffff',
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.6)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.9)',
                  borderColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#ffffff',
                  transform: 'scale(1.1)',
                },
                '&:focus-visible': {
                  outline: '3px solid rgba(255, 255, 255, 0.9)',
                  outlineOffset: '2px',
                },
                zIndex: 1,
                transition: 'all 0.2s ease',
              }}
            >
              <Close fontSize="large" />
            </IconButton>

            {/* Previous Button */}
            {images.length > 1 && (
              <IconButton
                onClick={handlePrevImage}
                onTouchStart={handlePrevImage}
                aria-label={intl.formatMessage({
                  id: 'projectModal.previousImage',
                  defaultMessage: 'Previous image',
                })}
                sx={{
                  position: 'absolute',
                  left: { xs: 8, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid rgba(255, 255, 255, 0.8)',
                    outlineOffset: '2px',
                  },
                  zIndex: 1,
                  transition: 'all 0.2s ease',
                }}
              >
                <KeyboardArrowLeft sx={{ fontSize: { xs: 28, sm: 32 } }} />
              </IconButton>
            )}

            {/* Image */}
            {selectedImageIndex !== null && (
              <Box sx={{ position: 'relative', maxWidth: '100%', maxHeight: '90vh' }}>
                <Box
                  component="img"
                  src={images[selectedImageIndex]?.path}
                  alt={
                    images[selectedImageIndex]?.label ||
                    intl.formatMessage(
                      {
                        id: 'projectModal.feature',
                        defaultMessage: 'Feature {number}',
                      },
                      { number: selectedImageIndex + 1 }
                    )
                  }
                  sx={{
                    maxWidth: '100%',
                    maxHeight: { xs: '85vh', sm: '90vh' },
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                />
                {/* Image Label */}
                {images[selectedImageIndex]?.label && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: { xs: 12, sm: 16 },
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 0.75, sm: 1 },
                      borderRadius: 2,
                      maxWidth: '85%',
                      textAlign: 'center',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 500 }}
                    >
                      {images[selectedImageIndex].label}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Next Button */}
            {images.length > 1 && (
              <IconButton
                onClick={handleNextImage}
                onTouchStart={handleNextImage}
                aria-label={intl.formatMessage({
                  id: 'projectModal.nextImage',
                  defaultMessage: 'Next image',
                })}
                sx={{
                  position: 'absolute',
                  right: { xs: 8, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid rgba(255, 255, 255, 0.8)',
                    outlineOffset: '2px',
                  },
                  zIndex: 1,
                  transition: 'all 0.2s ease',
                }}
              >
                <KeyboardArrowRight sx={{ fontSize: { xs: 28, sm: 32 } }} />
              </IconButton>
            )}

            {/* Image Counter */}
            {images.length > 1 && selectedImageIndex !== null && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { xs: 60, sm: 16 },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.75, sm: 1 },
                  borderRadius: 2,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                }}
                aria-live="polite"
                aria-atomic="true"
              >
                {selectedImageIndex + 1} / {images.length}
              </Box>
            )}
          </Box>
        </Modal>

      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          position: 'relative',
          zIndex: 1,
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          startIcon={<Close />}
          aria-label={intl.formatMessage({
            id: 'projectModal.close',
            defaultMessage: 'Close',
          })}
          sx={{
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            fontWeight: 600,
            px: 3,
            py: 1.2,
            borderRadius: '8px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.85)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
            },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'rgba(0, 0, 0, 0.8)',
              outlineOffset: '2px',
            },
          }}
        >
          <FormattedMessage id="projectModal.close" defaultMessage="Close" />
        </Button>
        <Button
          onClick={handleOpenWebsite}
          variant="contained"
          endIcon={<OpenInNew />}
          aria-label={intl.formatMessage({
            id: 'projectModal.visitWebsite',
            defaultMessage: 'Visit Website',
          })}
          sx={{
            background: gradientBackground,
            color: 'white',
            fontWeight: 600,
            px: 3,
            py: 1.2,
            borderRadius: '8px',
            boxShadow: `0 4px 15px ${gradientColors[0]}40`,
            transition: 'all 0.3s ease',
            '&:hover': {
              background: `linear-gradient(135deg, ${gradientColors[1]} 0%, ${gradientColors[0]} 100%)`,
              transform: 'translateY(-2px)',
              boxShadow: `0 6px 20px ${gradientColors[0]}60`,
            },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: gradientColors[0],
              outlineOffset: '2px',
            },
          }}
        >
          <FormattedMessage id="projectModal.visitWebsite" defaultMessage="Visit Website" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectModal;
